import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import * as styles from './Image.css';
import { dt } from '../../../helpers/Utils/GetTailwindToken';

const cx = classNames.bind(styles);
class Image extends Component {
    static propTypes = {
        alt: PropTypes.string,
        src: PropTypes.string,
        slickImage: PropTypes.bool,

        /**
         * Ratio of the image height to width
         *
         * http://aspiringwebdev.com/stop-your-web-pages-from-jumping-around-while-images-load/
         *
         * The page jumps occur because the browser does not know the width and height of the images in advance.
         * On a responsive site, the image could be a different height and width depending on the screen size.
         * We may, however, know the ratio of the image’s height to its width. That will not change for a given image, no matter the screen size
         * It’s easy to find an images height to width ratio:
         * Image height / Image width * 100
         * For the images in our example, image height: 217 and width: 325 we found the ratio:
         * 217px / 325px * 100 = 66.769230769
         *
         * Caveats:
         *   To accomodate different aspect ratios, create multiple image containers and use them where appropriate.
         *   Use this technique only where the aspect ratio is certain; images that don’t fit the aspect ratio will look distorted.
         *   A common challenge in responsive design – not just with this technique – is maintaining the right image quality for each screen.
         *
         *   Check out the Smashing Magazine article below for ideas on this.
         *   Thanks to Smashing Magazine for this technique. Check out a case study of a news site that implemented it successfully, and a similar technique for embedded content.
         *   http://www.smashingmagazine.com/2013/09/16/responsive-images-performance-problem-case-study/
         *   http://www.smashingmagazine.com/2014/02/27/making-embedded-content-work-in-responsive-design/
         * @type {[type]}
         */
        imageRatio: PropTypes.number,
        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */

        automationId: PropTypes.string,

        animate: PropTypes.bool,
        onImageLoad: PropTypes.func,
        onImageError: PropTypes.func,
        loader: PropTypes.bool,
        title: PropTypes.string,
        imgClass: PropTypes.string,
        width: PropTypes.string,
        height: PropTypes.string,
        /** If isLazy is true set loading to lazy, else null
         */
        isLazy: PropTypes.bool,
        renderFromPictureComponent: PropTypes.bool,
    };

    static defaultProps = {
        alt: '',
        title: '',
        src: '../images/placeholder.png', // This asset needs to added as per UX
        imageRatio: 100,
        automationId: '',
        animate: false,
        loader: false,
        onImageLoad: () => {},
        onImageError: () => {},
        imgClass: '',
        slickImage: false,
        width: '',
        height: '',
        isLazy: false,
        renderFromPictureComponent: false,
    };

    constructor() {
        super();
        this.state = {
            loading: true,
        };
        this.imageRef = React.createRef();
    }

    componentDidMount() {
        const { onImageLoad } = this.props;
        if (this.imageRef?.current?.complete) {
            onImageLoad && onImageLoad(this.imageRef?.current);
        }
    }

    removeLoaderAndTriggerEvent = (eventToTrigger, target) => {
        const { loader } = this.props;
        if (loader) {
            this.setState({
                loading: false,
            });
        }
        eventToTrigger(target);
    };

    render() {
        const {
            alt,
            imageRatio,
            automationId,
            onImageLoad,
            onImageError,
            loader,
            title,
            imgClass,
            slickImage,
            width,
            height,
            isLazy,
            renderFromPictureComponent,
        } = this.props;
        const src = this.props.src ? this.props.src.replace(/^http[s]?:/, '') : '';

        const imageBoxMainStyle = dt(['overflow-y-hidden', 'h-0', 'relative']);
        const imageClassStyle = dt(['absolute', 'top-0', 'left-0', 'w-full', 'h-auto']);
        const imageTagStyle = dt(['h-auto', 'w-full']);
        const displayRadiusStyle = dt(['rounded-lg']);

        // Wrap this css to stop the page jump around when images are loading
        const imageWrapperClass = cx({
            [imageBoxMainStyle]: imageRatio > 0,
            [displayRadiusStyle]: slickImage,
            [styles.loader]: this.state.loading && loader,
        });

        // This class is for the image
        const imageClass = cx({
            [imageClassStyle]: imageRatio > 0,
            [imageTagStyle]: imageRatio <= 0,
        });
        // We have to add this styling to div once we figure out the imageRatio issue fix.
        const imageStyle = {};

        if (this.props.imageRatio > 0) {
            imageStyle.paddingBottom = `${this.props.imageRatio}%`;
        }

        const animateClass = cx({
            [styles.imageAnim]: this.props.animate,
        });

        const loading = isLazy ? 'lazy' : null;

        const img = (
            <img
                className={
                    renderFromPictureComponent
                        ? dt(['w-full'])
                        : cx(imageClass, animateClass, imgClass)
                }
                ref={this.imageRef}
                loading={loading}
                src={src}
                title={title}
                width={width}
                height={height}
                alt={alt}
                data-automation-id={automationId}
                onLoad={({ target }) => this.removeLoaderAndTriggerEvent(onImageLoad, target)}
                onError={() => this.removeLoaderAndTriggerEvent(onImageError)}
            />
        );

        if (renderFromPictureComponent) {
            return <>{img}</>;
        }

        return imageWrapperClass ? (
            <div className={imageWrapperClass} style={imageStyle}>
                {img}
            </div>
        ) : (
            <>{img}</>
        );
    }
}
export default Image;
